import type { Session, User } from '@supabase/supabase-js';
import {
  PARENT_URL,
  getAdminUrlForRoles,
  getStudentHandoffUrl,
} from '../config/companies.ts';
import type { RoleRow } from '../config/companies.ts';
import { supabase, companyDb } from '../supabaseClient.ts';
import { handoffTo, STUDENT_HOME } from './handoff.ts';
import { findStudentId, provisionStudentAccount } from './provisionStudent.ts';

export type AreaChoice = {
  session: Session;
  adminUrl: string;
  studentHandoffUrl: string | null;
};

export type RouteAfterSignIn =
  | { type: 'student'; handoffUrl: string | null }
  | { type: 'handoff'; url: string }
  | { type: 'choice'; adminUrl: string; studentHandoffUrl: string | null }
  | { type: 'complete_signup' }
  | { type: 'error'; message: string };

/**
 * A public Google (or unfinished email) signup has a session but no student
 * row and no staff/parent role yet — they still need year + subjects.
 */
export async function needsSignupCompletion(user: User): Promise<boolean> {
  if (await findStudentId(user.id)) return false;

  const { data: roles } = await supabase
    .from('roles_v')
    .select('role')
    .eq('user_id', user.id);
  if ((roles ?? []).length > 0) return false;

  const meta = user.user_metadata ?? {};
  // Email confirmation already collected year/subjects; WelcomePage provisions.
  if (meta.signup_role === 'student' && meta.signup_year_id) return false;

  return true;
}

/**
 * Same role routing the login form uses, so Google and email confirmation
 * land in the same place a password sign-in would.
 */
export async function routeAfterSignIn(
  user: User,
  options?: { allowCompleteSignup?: boolean },
): Promise<RouteAfterSignIn> {
  const allowCompleteSignup = options?.allowCompleteSignup ?? false;

  const { data: roles, error: rolesErr } = await supabase
    .from('roles_v')
    .select('role,company_id')
    .eq('user_id', user.id);

  if (rolesErr) {
    await supabase.auth.signOut();
    return { type: 'error', message: rolesErr.message };
  }

  const roleRows = (roles ?? []) as RoleRow[];
  const heldRoles = new Set(roleRows.map((r) => r.role));
  const isAdminLike = heldRoles.has('admin') || heldRoles.has('tutor');
  const isStudent = heldRoles.has('student');
  const isParent = heldRoles.has('parent');

  let studentAllowed = true;
  let adminAllowed = true;

  if (isStudent) {
    const { data: studentStatus, error: studentErr } = await supabase
      .from('students')
      .select('is_active')
      .eq('user_id', user.id)
      .maybeSingle();

    if (studentErr) {
      await supabase.auth.signOut();
      return { type: 'error', message: 'Login failed.' };
    }

    if (studentStatus && studentStatus.is_active === false) {
      studentAllowed = false;
    }
  }

  if (isAdminLike) {
    const staffCompanyIds = Array.from(
      new Set(
        roleRows
          .filter((r) => r.role === 'admin' || r.role === 'tutor')
          .map((r) => r.company_id)
          .filter((id): id is string => !!id),
      ),
    );

    if (staffCompanyIds.length > 0) {
      const { data: staffRows, error: staffErr } = await companyDb
        .from('staff_profiles')
        .select('company_id,is_active')
        .eq('user_id', user.id)
        .in('company_id', staffCompanyIds);

      if (staffErr) {
        await supabase.auth.signOut();
        return { type: 'error', message: 'Login failed.' };
      }

      const rows = staffRows ?? [];
      if (rows.length > 0 && rows.every((r) => !r.is_active)) {
        adminAllowed = false;
      }
    }
  }

  const adminUrl = getAdminUrlForRoles(roleRows);
  const studentHandoffUrl = getStudentHandoffUrl(roleRows);

  const deactivated = async (): Promise<RouteAfterSignIn> => {
    await supabase.auth.signOut();
    return { type: 'error', message: 'Sorry, this account has been deactivated.' };
  };

  if (isAdminLike && isStudent) {
    if (adminAllowed && studentAllowed) {
      return { type: 'choice', adminUrl, studentHandoffUrl };
    }
    if (adminAllowed) return { type: 'handoff', url: adminUrl };
    if (studentAllowed) return { type: 'student', handoffUrl: studentHandoffUrl };
    return deactivated();
  }

  if (isAdminLike) {
    if (!adminAllowed) return deactivated();
    return { type: 'handoff', url: adminUrl };
  }

  if (isStudent) {
    if (!studentAllowed) return deactivated();
    return { type: 'student', handoffUrl: studentHandoffUrl };
  }

  if (isParent) {
    return { type: 'handoff', url: PARENT_URL };
  }

  try {
    if (await provisionStudentAccount(user)) {
      return { type: 'student', handoffUrl: studentHandoffUrl };
    }
  } catch (err) {
    await supabase.auth.signOut();
    return {
      type: 'error',
      message: err instanceof Error ? err.message : 'Could not finish setting up your account.',
    };
  }

  if (allowCompleteSignup) {
    return { type: 'complete_signup' };
  }

  await supabase.auth.signOut();
  return { type: 'error', message: "Your account doesn't have a role yet. Please contact support." };
}

export function applySignedInRoute(
  result: RouteAfterSignIn,
  session: Session,
  handlers: {
    navigate: (path: string, opts?: { replace?: boolean }) => void;
    setChoice: (choice: AreaChoice) => void;
    setError: (message: string) => void;
  },
): void {
  switch (result.type) {
    case 'student':
      if (result.handoffUrl) {
        handoffTo(result.handoffUrl, session);
        return;
      }
      handlers.navigate(STUDENT_HOME, { replace: true });
      return;
    case 'handoff':
      handoffTo(result.url, session);
      return;
    case 'choice':
      handlers.setChoice({
        session,
        adminUrl: result.adminUrl,
        studentHandoffUrl: result.studentHandoffUrl,
      });
      return;
    case 'complete_signup':
      handlers.navigate('/signup/complete', { replace: true });
      return;
    case 'error':
      handlers.setError(result.message);
      return;
  }
}
